import React, { useCallback, useMemo, useRef, useState } from 'react';

import {
	useClassNames,
} from '@common';

import './progress-bar.scss';

interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
	/** newHoverProgress will be int from 0 to 100 */
	onHoverProgressChange?: (newHoverProgress: number) => void,
	/** newProgressValue will be int from 0 to 100 */
	onProgressChange?: (newProgressValue: number) => void,
	hoverProgressText?: string | number,
	progressText?: string | number,
	/** measured from 0 to 100 */
	hoverProgress?: number,
	/** measured from 0 to 100 */
	progress: number,

	timecodeStart?:number,
	timecodeEnd?:number,

	showHoverProgress?: boolean,
	position?: 'horizontal' | 'vertical',

	disabled?: boolean,
}

function ProgressBar(props: ProgressBarProps): JSX.Element {
	const {
		onHoverProgressChange,
		hoverProgressText,
		onProgressChange,
		hoverProgress,
		progressText,
		progress,
		timecodeStart,
		timecodeEnd,
		showHoverProgress = false,
		onMouseOver,
		onMouseDown,
		className,
		position = 'horizontal',
		disabled = false,
		...otherProps
	} = props;

	const
		[isPressed, setIsPressed] = useState<boolean>(false),
		progressWrapperRef = useRef<HTMLDivElement>(null);

	const handleChangeProgress = useCallback((newCursorPosition: number) => {
		let newProgressValue: number;

		switch (position) {
			case 'vertical':
				newProgressValue = newCursorPosition / progressWrapperRef.current.offsetHeight * 100;
				break;
			case 'horizontal':
			default:
				newProgressValue = newCursorPosition / progressWrapperRef.current.offsetWidth * 100;
				break;
		}

		if (newProgressValue > 100) {
			newProgressValue = 100;
		} else if (newProgressValue < 0) {
			newProgressValue = 0;
		}

		onProgressChange(newProgressValue);
	}, [onProgressChange, position]);

	const handleChangeHoverProgress = useCallback((newCursorPosition: number) => {
		let newHoverProgressValue: number;

		switch (position) {
			case 'vertical':
				newHoverProgressValue = newCursorPosition / progressWrapperRef.current.offsetHeight * 100;
				break;
			case 'horizontal':
			default:
				newHoverProgressValue = newCursorPosition / progressWrapperRef.current.offsetWidth * 100;
				break;
		}

		if (newHoverProgressValue > 100) {
			newHoverProgressValue = 100;
		} else if (newHoverProgressValue < 0) {
			newHoverProgressValue = 0;
		}

		onHoverProgressChange(newHoverProgressValue);
	}, [onHoverProgressChange, position]);

	// LATER: debounce
	const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (disabled) {
			return;
		}

		onMouseDown && onMouseDown(e);
		if (!progressWrapperRef.current) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();

		setIsPressed(true);

		switch (position) {
			case 'vertical':
				handleChangeProgress(progressWrapperRef.current.getBoundingClientRect().bottom - e.pageY);
				break;
			case 'horizontal':
			default:
				handleChangeProgress(e.nativeEvent.offsetX);
				break;
		}

		function onMouseMove(moveEvent) {
			let containerBorderCoordinate: number;
			let newCursorPosition: number;

			switch (position) {
				case 'vertical':
					containerBorderCoordinate = progressWrapperRef.current.getBoundingClientRect().bottom;
					newCursorPosition = containerBorderCoordinate - moveEvent.pageY;
					break;
				case 'horizontal':
				default:
					containerBorderCoordinate = progressWrapperRef.current.getBoundingClientRect().left;
					newCursorPosition = moveEvent.pageX - containerBorderCoordinate;
					break;
			}

			handleChangeProgress(newCursorPosition);
		}

		function removeListeners() {
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseup', removeListeners);
			setIsPressed(false);
		}

		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', removeListeners);

		// handleChangeProgress(e.nativeEvent.offsetX);
	}, [handleChangeProgress, position, onMouseDown, disabled]);

	// LATER: debounce
	const handleMouseOver = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
		if (disabled) {
			return;
		}

		onMouseOver && onMouseOver(e);

		if (!showHoverProgress || !progressWrapperRef.current) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();

		switch (position) {
			case 'vertical':
				handleChangeHoverProgress(e.nativeEvent.offsetY);
				break;
			case 'horizontal':
			default:
				handleChangeHoverProgress(e.nativeEvent.offsetX);
				break;
		}

		function onMouseMove(moveEvent) {
			let containerBorderCoordinate: number;
			let newCursorPosition: number;

			switch (position) {
				case 'vertical':
					containerBorderCoordinate = progressWrapperRef.current.getBoundingClientRect().bottom;
					newCursorPosition = containerBorderCoordinate - moveEvent.pageY;
					break;
				case 'horizontal':
				default:
					containerBorderCoordinate = progressWrapperRef.current.getBoundingClientRect().left;
					newCursorPosition = moveEvent.pageX - containerBorderCoordinate;
					break;
			}

			handleChangeHoverProgress(newCursorPosition);
		}

		function removeListeners() {
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseout', removeListeners);
		}

		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseout', removeListeners);

		// handleChangeHoverProgress(e.nativeEvent.offsetX);
	}, [handleChangeHoverProgress, onMouseOver, position, showHoverProgress,
		disabled]);

	const
		classes = useClassNames(
			isPressed && 'pressed',
			'progress-bar',
			disabled && 'progress-bar_disabled',
			position,
			);
			
	const wrapperClasses = useClassNames(
			'progress-bar__wrapper',
			disabled && 'progress-bar__wrapper_disabled',
			className,
			position,
		);

	const progressView = useMemo((): JSX.Element => {
		const progressStyles: React.CSSProperties = {};

		switch (position) {
			case 'vertical':
				progressStyles.height = `${progress || 0}%`;
				break;
			case 'horizontal':
			default:
				progressStyles.width = `${progress || 0}%`;
				break;
		}

		return (
			<div
				className="progress-bar__progress"
				style={progressStyles}
			>
				{progressText && <div className="progress-bar__progress-text">
					{isPressed && progressText}
				</div>}
			</div>
		);
	}, [progress, isPressed, progressText, position]);

	const hoverProgressView = useMemo(():JSX.Element => {
		const hoverProgressStyles: React.CSSProperties = {};

		switch (position) {
			case 'vertical':
				hoverProgressStyles.height = `${hoverProgress || 0}%`;
				break;
			case 'horizontal':
			default:
				hoverProgressStyles.width = `${hoverProgress || 0}%`;
				break;
		}

		return (
			<div
				className="progress-bar__hover-progress"
				style={hoverProgressStyles}
			>
				{hoverProgressText &&
				<div className="progress-bar__progress-text">
					{hoverProgressText}
				</div>}
			</div>
		);
	}, [hoverProgress, hoverProgressText, position]);

	const timeCodesView = useMemo(():JSX.Element => {
		const startTimeStyle: React.CSSProperties = {};
		switch (position) {
			case 'vertical':
				startTimeStyle.height = `${timecodeStart || 0}%`;
				break;
			case 'horizontal':
			default:
				startTimeStyle.width = `${timecodeStart || 0}%`;
				break;
		}

		const endTimeStyle: React.CSSProperties = {};
		switch (position) {
			case 'vertical':
				endTimeStyle.height = `${timecodeEnd || 0}%`;
				break;
			case 'horizontal':
			default:
				endTimeStyle.width = `${timecodeEnd || 0}%`;
				break;
		}

		return <>
			<div
				className="progress-bar__timecode"
				style={startTimeStyle}
			/>
			<div
				className="progress-bar__timecode"
				style={endTimeStyle}
			/>
			</>
	}, [timecodeStart, timecodeEnd, position]);
	

	return (
		<div
			className={wrapperClasses}
			onMouseDown={handleMouseDown}
			onMouseOver={handleMouseOver}
			ref={progressWrapperRef}>
			<div
				className={classes}
				{...otherProps}
			>
				{progressView}
				{!disabled && showHoverProgress && hoverProgressView}
				{timecodeStart && timecodeEnd && timeCodesView}
			</div>
		</div>
	);
}

export {ProgressBar};